<template lang="pug">
.comm-link-picker
    el-radio-group.comm-link-picker__types(v-model="linkType" size="mini" @change="onTypeChange")
        el-radio-button(v-if="has('menuItem')" label="internal") {{ $t('Internal') }}
        el-radio-button(v-if="has('categoryPage')" label="category") {{ $t('Post') }}
        el-radio-button(v-if="has('product')" label="product") {{ $t('Product') }}
        el-radio-button(label="external") {{ $t('External') }}
        el-radio-button(label="mailto") {{ $t('Email') }}
        el-radio-button(label="media") {{ $t('Media library') }}
        el-radio-button(v-if="allowOrder && has('order')" label="order") {{ $t('Order details') }}
        //- Tracking bakes the API /track redirect (not a storefront link), so it needs no
        //- `order` link pattern — offered whenever an order is in scope.
        el-radio-button(v-if="allowOrder" label="tracking") {{ $t('Track shipment') }}

    .comm-link-picker__body
        Treeselect(
            v-if="linkType === 'internal'"
            v-model="internalUrl"
            :options="menuTree"
            :multiple="false"
            :searchable="true"
            :clearable="true"
            :open-on-focus="true"
            :default-expand-level="Infinity"
            :normalizer="node => ({ label: node.title, ...node })"
            :placeholder="$t('Choose a page')"
            @input="emitValue"
        )
            label(slot="option-label" slot-scope="{ node }" :class="{ optgroup: node.raw.optgroup }")
                span {{ node.raw.title }}
            div(slot="value-label" slot-scope="{ node }")
                span {{ node.raw.title }}
        el-select.is-full-width(
            v-else-if="linkType === 'category'"
            v-model="categoryUrl"
            filterable
            remote
            clearable
            size="small"
            :remote-method="searchCategoryPages"
            :loading="categoriesLoading"
            :placeholder="$t('Search for a post')"
            @change="emitValue"
        )
            el-option(v-for="page in categoryPages" :key="page.id" :label="page.title || page.key" :value="page.url")
                post-option-preview(:post="page")
        el-select.is-full-width(
            v-else-if="linkType === 'product'"
            v-model="productUrl"
            filterable
            remote
            clearable
            size="small"
            :remote-method="searchProducts"
            :loading="productsLoading"
            :placeholder="$t('Search for a product')"
            @change="emitValue"
        )
            el-option(v-for="product in products" :key="product.id" :label="product.name" :value="product.url")
                product-option-preview(:image="product.image" :topic="product.category" :aux="product.number" :title="product.name" :title-aux="product.price" :teaser="product.description")
        el-input(
            v-else-if="linkType === 'external'"
            v-model="externalUrl"
            size="small"
            placeholder="https://…"
            @input="emitValue"
        )
        el-input(
            v-else-if="linkType === 'mailto'"
            v-model="email"
            size="small"
            placeholder="name@example.com"
            @input="emitValue"
        )
        .comm-link-picker__media(v-else-if="linkType === 'media'")
            .flex-row
                el-input.is-full-width(size="small" :value="mediaName" :placeholder="$t('No file selected')" disabled)
                el-button(plain size="small" icon="el-icon-fa-folder-open fas" @click="mediaVisible = true") {{ mediaName ? $t('Change file') : $t('Open media library') }}
            span.help.comm-link-picker__temp-hint(v-if="mediaIsTemporary")
                i.el-icon-fa-clock.fas
                | {{ $t('This is a private file; the link works for a limited time only.') }}
            image-library-dialog(:visible.sync="mediaVisible" selection-type="file" @select="onMediaSelect")
        .comm-link-picker__order(v-else-if="linkType === 'order' || linkType === 'tracking'")
            //- Template (order-agnostic, no member): the button is DYNAMIC — the recipient's OWN
            //- order is filled in per-send by the backend, so there's nothing to pick here.
            template(v-if="isDynamicOrder")
                span.help(v-if="linkType === 'order'") {{ $t('The recipient\'s own order details are filled in automatically when the template is sent. Nothing to choose here.') }}
                span.help(v-else) {{ $t('The recipient\'s own parcel tracking is filled in automatically when the template is sent. Nothing to choose here.') }}
            //- Composer (a chat with a member): a chat spans many orders, so the agent picks WHICH
            //- order this button points at; the signed URL is minted server-side from it at send.
            template(v-else)
                el-select.is-full-width(
                    v-model="orderId"
                    filterable
                    clearable
                    size="small"
                    :loading="ordersLoading"
                    :placeholder="$t('Choose an order')"
                    @change="emitValue"
                    @visible-change="onOrderDropdownVisible"
                )
                    el-option(v-for="order in orders" :key="order.id" :label="orderReference(order)" :value="order.id")
                        product-option-preview(:title="orderReference(order)" :aux="orderMeta(order)")
                span.help(v-if="linkType === 'order'") {{ $t('Links to the chosen order; the personal, signed address is filled in when you send.') }}
                span.help(v-else) {{ $t('Redirects to the chosen order\'s parcel tracking; the shipment status is filled in when you send.') }}
</template>

<i18n>
{
    "de": {
        "Internal": "Interne Seite",
        "Post": "Beitrag",
        "Product": "Produkt",
        "External": "Externer Link",
        "Email": "E-Mail",
        "Media library": "Medienbibliothek",
        "Order details": "Bestelldetails",
        "Track shipment": "Sendungsverfolgung",
        "Choose an order": "Bestellung auswählen",
        "Choose a page": "Seite auswählen",
        "Search for a post": "Beitrag suchen …",
        "Search for a product": "Produkt suchen …",
        "No file selected": "Keine Datei ausgewählt",
        "Open media library": "Medienbibliothek öffnen",
        "Change file": "Datei ändern",
        "This is a private file; the link works for a limited time only.": "Dies ist eine private Datei; der Link funktioniert nur für begrenzte Zeit.",
        "Nr": "Nr.",
        "Links to the chosen order; the personal, signed address is filled in when you send.": "Verlinkt die ausgewählte Bestellung; die persönliche, signierte Adresse wird beim Senden eingefügt.",
        "Redirects to the chosen order's parcel tracking; the shipment status is filled in when you send.": "Leitet zur Sendungsverfolgung der ausgewählten Bestellung weiter; der Versandstatus wird beim Senden eingefügt.",
        "The recipient's own order details are filled in automatically when the template is sent. Nothing to choose here.": "Die Bestelldetails des jeweiligen Empfängers werden beim Senden der Vorlage automatisch eingefügt. Hier gibt es nichts auszuwählen.",
        "The recipient's own parcel tracking is filled in automatically when the template is sent. Nothing to choose here.": "Die Sendungsverfolgung des jeweiligen Empfängers wird beim Senden der Vorlage automatisch eingefügt. Hier gibt es nichts auszuwählen."
    }
}
</i18n>

<script>
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import MenuApi from '@/store/api/cms/menu'
import PageApi from '@/store/api/cms/page'
import ProductApi from '@/extensions/shop/store/api/product'
import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog'
import PostOptionPreview from '@/components/Page/Sections/Partials/PostOptionPreview'
import ProductOptionPreview from './ProductOptionPreview'
import ConversationApi from '@/extensions/communication/store/api/conversation'
import { createTree } from '@/nested-set-lib'
import { useSetupStore, usePageStore } from '@/stores'
import { moneyFormat } from '@/filters'
import { orderReference, orderMeta } from '@/extensions/communication/utils/orderOption'

export default {
    name: 'LinkPicker',
    components: { Treeselect, ImageLibraryDialog, ProductOptionPreview, PostOptionPreview },
    props: {
        // v-model — the resolved link: a literal storefront URL (internal/category/product/
        // external/mailto/media) or the {{order.details_url}} token (purchase details).
        value: {
            type: String,
            default: ''
        },
        // Offer the "Order details" / "Track shipment" links (only when the thread has a
        // member whose orders we can list).
        allowOrder: {
            type: Boolean,
            default: false
        },
        // The conversation's member — the source of the selectable orders for the
        // per-message order/tracking link. A chat isn't tied to one order, so the agent
        // picks WHICH order each order/tracking button points at.
        memberId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            linkType: 'external',
            menus: [],
            categoryPages: [],
            categoriesLoading: false,
            products: [],
            productsLoading: false,
            internalUrl: null,
            categoryUrl: null,
            productUrl: null,
            externalUrl: '',
            email: '',
            orders: [],
            orderId: null,
            ordersLoading: false,
            mediaUrl: '',
            mediaName: '',
            mediaVisible: false,
            // A private file was linked via its short-lived signed url → warn the admin.
            mediaIsTemporary: false,
            // The last value WE emitted. internal/category/product/media all resolve to a
            // plain storefront URL that hydrateFromValue can't tell from an external one, so
            // without this guard the parent's v-model round-trip would re-hydrate every pick
            // back to "External". We skip hydration when the incoming value is our own echo.
            lastEmitted: null
        }
    },
    computed: {
        activeCms() {
            return useSetupStore().activeCms
        },
        // Dynamic-order mode: an order button is allowed but there's no member to scope orders to
        // (a reusable WhatsApp template, as opposed to a member chat). The button becomes the bare
        // {{order.details_url}} / {{order.tracking_url}} token — the backend fills the recipient's
        // OWN order per-send (OrderNotificationWhatsAppSender) — so there's no order to pick.
        isDynamicOrder() {
            return this.allowOrder && !this.memberId
        },
        // The tenant's storefront base (https://host), used to fill the `{domain}` in every
        // storefront link pattern.
        storefrontBase() {
            const base = _.get(this.activeCms, 'domains.0.base_url')
            return base ? `https://${base.replace(/^https?:\/\//, '').replace(/\/$/, '')}` : ''
        },
        // The template's declared storefront links (menuItem/categoryPage/product/order) with
        // their URL patterns. This array IS the contract for what's offered + how it's built.
        templateLinks() {
            return _.get(this.activeCms, 'config.template.links') || []
        },
        // Menu items as a Treeselect tree, each leaf's value the full URL (menuItem pattern,
        // {path} = the item's path). Mirrors the reference link tool exactly: each menu is a
        // greyed, non-selectable OPT-GROUP header followed by its NestedSet item tree as
        // siblings (not nested under the menu) — group headers only when >1 menu has items.
        menuTree() {
            const pattern = this.patternFor('menuItem')
            if (!pattern || !this.storefrontBase) return []
            // A NestedSet item node → a treeselect node whose id is the resolved URL and whose
            // `title` drives the label/search (via the normalizer).
            const toNode = item => {
                const node = { id: this.fill(pattern, { path: item.path }), title: item.title }
                const children = (item.children || []).filter(child => child.path).map(toNode)
                if (children.length) node.children = children
                return node
            }
            const menusWithItems = this.menus.filter(menu => (menu.menuItems || []).some(item => item.path))
            const showGroups = menusWithItems.length > 1
            const tree = []
            this.menus.forEach(menu => {
                const items = createTree(_.cloneDeep((menu.menuItems || []).filter(item => item.path)))
                const nodes = items.filter(item => item.path).map(toNode)
                if (!nodes.length) return
                if (showGroups) {
                    tree.push({ id: `optgroup-${menu.id}`, title: menu.name, isDisabled: true, optgroup: true })
                }
                tree.push(...nodes)
            })
            return tree
        }
    },
    watch: {
        value(next) {
            // Our own emit echoing back → keep the chosen type (don't snap to External).
            if (next === this.lastEmitted) return
            this.hydrateFromValue()
        },
        memberId() {
            this.loadOrders()
        }
    },
    mounted() {
        this.loadSources()
        this.loadOrders()
        this.hydrateFromValue()
        // Pre-populate the select if the picker opens already on a post/product link.
        this.ensureTypeOptions()
    },
    methods: {
        // Whether the storefront template declares a given link type.
        has(key) {
            return this.templateLinks.some(link => link.key === key)
        },
        patternFor(key) {
            const link = this.templateLinks.find(entry => entry.key === key)
            return link ? link.pattern : null
        },
        // Interpolate a link pattern: always fills {domain}; the rest from `values`.
        fill(pattern, values) {
            let url = String(pattern || '').replace('{domain}', this.storefrontBase)
            Object.keys(values || {}).forEach(name => {
                url = url.replace(`{${name}}`, values[name])
            })
            return url
        },
        loadSources() {
            const cms = this.activeCms
            if (!cms) return
            if (this.has('menuItem')) {
                MenuApi.fetchList(cms.id).then(menus => { this.menus = menus || [] }).catch(() => { this.menus = [] })
            }
        },
        // Remote CATEGORY-PAGE search: content pages under the template's categories, matched
        // by title — the canonical `posts`-style mechanism. Value = the full storefront URL
        // (categoryPage pattern, {category} = the category key, {key} = the page key).
        searchCategoryPages(query) {
            const pattern = this.patternFor('categoryPage')
            const cms = this.activeCms
            if (!pattern || !cms) return
            const categories = _.get(cms, 'config.template.categories') || []
            this.categoriesLoading = true
            // Same fetch the reference link tool uses: full pages (sections, category,
            // created_at) newest-first so the canonical PostOptionPreview renders them exactly
            // like the reference (image + category + date + title + teaser). perPage 100 + an
            // empty query pre-populates the list (no forced search).
            PageApi.fetchPagesPaginated({
                pageInfo: { perPage: 100 },
                filters: [
                    { key: 'title', like: query || '' },
                    { key: 'cms', match: cms.id },
                    { key: 'category_keys', in: categories.map(category => category.key) }
                ],
                variables: { order: 'created_at', direction: 'desc', withCategory: true }
            })
                .then(result => {
                    this.categoryPages = (_.get(result, 'items') || [])
                        .filter(page => page.key && _.get(page, 'category.key'))
                        .map(page => ({ ...page, url: this.fill(pattern, { category: page.category.key, key: page.key }) }))
                })
                .catch(() => { this.categoryPages = [] })
                .finally(() => { this.categoriesLoading = false })
        },
        // Remote product search → each option's value is the full storefront URL (product
        // pattern, {slug} = the slug). Enriched with nr + price + image so results are
        // recognizable (needs `extended` for price/media).
        searchProducts(query) {
            const pattern = this.patternFor('product')
            const cms = this.activeCms
            if (!pattern || !cms) return
            const term = (query || '').trim()
            this.productsLoading = true
            // An empty term pre-populates with the newest products (mirrors the post select),
            // so the field never forces a search first.
            ProductApi.fetchProductsPaginated({
                scope: 'ex',
                type: 'shop_product',
                pageInfo: { currentPage: 1, perPage: term ? 20 : 100 },
                order: 'created_at',
                direction: 'desc',
                filters: term ? [{ key: 'name', match: term }] : []
            }, false, true)
                .then(result => {
                    this.products = (_.get(result, 'items') || [])
                        .filter(product => product.slug)
                        .map(product => ({
                            id: product.id,
                            name: product.name,
                            category: _.get(product, 'categories.0.name') || '',
                            number: product.nr ? `${this.$t('Nr')} ${product.nr}` : '',
                            price: this.productPrice(product),
                            description: this.productDescription(product),
                            image: this.productImage(product),
                            url: this.fill(pattern, { slug: product.slug })
                        }))
                })
                .catch(() => { this.products = [] })
                .finally(() => { this.productsLoading = false })
        },
        // The formatted price (top-right, like the post's date). Price is a VARCHAR money
        // value; moneyFormat is the app-wide currency formatter (ISO-4217 from the group).
        productPrice(product) {
            const value = _.get(product, 'price.value')
            if (value === null || value === undefined || value === '') return ''
            const currency = _.get(product, 'price.customerGroup.currency.iso4217') || 'EUR'
            return moneyFormat(value, currency)
        },
        // A short plain-text excerpt for the teaser line (mirrors the post's teaser).
        productDescription(product) {
            const html = _.get(product, 'description') || ''
            return html ? String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : ''
        },
        // First media image's displayable (processed/imgix) URL — NOT the raw source path,
        // which isn't browser-reachable in every environment.
        productImage(product) {
            const media = _.get(product, 'media') || []
            for (const entry of media) {
                const url = _.get(entry, 'image.processedFile.url')
                    || _.get(entry, 'image.activeVariant.file.url')
                    || _.get(entry, 'image.source.url')
                if (url) return url
            }
            return ''
        },
        // Map an incoming value back to a type + field so an existing link reopens on its own
        // type. A storefront URL IS recognizable — it carries the tenant domain and a declared
        // link structure — so it is reverse-matched against the template's patterns rather than
        // falling back to "External".
        hydrateFromValue() {
            const value = this.value || ''
            const detailsMatch = value.match(/^\{\{order\.details_url(?::(\d+))?\}\}$/)
            if (detailsMatch) {
                this.linkType = this.allowOrder && this.has('order') ? 'order' : 'external'
                this.orderId = detailsMatch[1] ? Number(detailsMatch[1]) : null
                return
            }
            const trackingMatch = value.match(/^\{\{order\.tracking_url(?::(\d+))?\}\}$/)
            if (trackingMatch) {
                this.linkType = this.allowOrder ? 'tracking' : 'external'
                this.orderId = trackingMatch[1] ? Number(trackingMatch[1]) : null
                return
            }
            if (value.startsWith('mailto:')) {
                this.linkType = 'mailto'
                this.email = value.slice('mailto:'.length)
                return
            }
            const matched = value ? this.matchStorefrontType(value) : null
            if (matched) {
                this.linkType = matched.type
                this[matched.field] = value
                this.seedHydratedOption(matched.type, value)
                return
            }
            if (value) {
                this.linkType = 'external'
                this.externalUrl = value
            }
        },
        // Reverse-match a URL against the template's declared storefront patterns. Each pattern
        // (e.g. "{domain}/shop/{slug}") becomes a regex with its fixed parts literal and its
        // {placeholders} as wildcards; the match with the most fixed text wins, so a product's
        // "/shop/x" beats the more generic post pattern for the same URL.
        matchStorefrontType(value) {
            const candidates = [
                { key: 'menuItem', type: 'internal', field: 'internalUrl' },
                { key: 'categoryPage', type: 'category', field: 'categoryUrl' },
                { key: 'product', type: 'product', field: 'productUrl' }
            ]
            let best = null
            candidates.forEach(candidate => {
                if (!this.has(candidate.key)) return
                const pattern = this.patternFor(candidate.key)
                if (!pattern || !this.storefrontBase) return
                const literalLength = this.patternLiteralLength(value, pattern)
                if (literalLength !== null && (!best || literalLength > best.literalLength)) {
                    best = { ...candidate, literalLength }
                }
            })
            return best
        },
        // The length of a pattern's FIXED text if `value` matches its shape, else null. Used to
        // rank overlapping patterns by specificity.
        patternLiteralLength(value, pattern) {
            const filled = String(pattern).replace('{domain}', this.storefrontBase)
            const literals = filled.split(/\{[a-z_]+\}/i)
            const regex = new RegExp('^' + literals.map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.+') + '$', 'i')
            return regex.test(value) ? literals.join('').length : null
        },
        // A hydrated product/post URL has no loaded option to display its name, so seed a single
        // option carrying the URL — the select then shows the link instead of a blank field.
        seedHydratedOption(type, value) {
            if (type === 'product' && !this.products.some(product => product.url === value)) {
                this.products = [{ id: `hydrated-${value}`, name: value, meta: '', image: '', url: value }, ...this.products]
            } else if (type === 'category' && !this.categoryPages.some(page => page.url === value)) {
                // Shape a minimal page PostOptionPreview can render without crashing (empty
                // sections → no image/teaser, just the title).
                this.categoryPages = [{ id: `hydrated-${value}`, title: value, key: value, sections: [], category: null, created_at: null, url: value }, ...this.categoryPages]
            }
        },
        onTypeChange() {
            this.ensureTypeOptions()
            this.emitValue()
        },
        // Pre-populate the current type's select (newest first) so it never forces a search —
        // the post/product lists show immediately, like the reference.
        ensureTypeOptions() {
            if (this.linkType === 'category' && !this.categoryPages.length) this.searchCategoryPages('')
            if (this.linkType === 'product' && !this.products.length) this.searchProducts('')
        },
        // The conversation member's recent orders — the choices for an order/tracking button.
        // Reuses the same fetch the "link to order" dialog uses (member-scoped).
        loadOrders() {
            if (!this.memberId) {
                this.orders = []
                return
            }
            this.ordersLoading = true
            ConversationApi.fetchPurchasesForLink({ member: this.memberId, take: 50 })
                .then(results => { this.orders = results || [] })
                .catch(() => { this.orders = [] })
                .finally(() => { this.ordersLoading = false })
        },
        // Lazy-load on first open in case the member arrived after mount.
        onOrderDropdownVisible(open) {
            if (open && !this.orders.length && !this.ordersLoading) this.loadOrders()
        },
        orderReference(order) {
            return orderReference(order)
        },
        orderMeta(order) {
            return orderMeta(order, this.$t('yyyy-MM-dd'))
        },
        onMediaSelect(file) {
            // A private file's plain url is not publicly fetchable (the customer gets "Access
            // denied"), so link to its short-lived SIGNED url instead and flag it as temporary.
            const isPrivate = !!file && file.visibility === 'private'
            this.mediaIsTemporary = isPrivate
            this.mediaUrl = (file && (isPrivate ? (file.signed_url || file.url) : file.url)) || ''
            this.mediaName = (file && (file.name || file.title)) || ''
            this.emitValue()
        },
        emitValue() {
            let resolved = ''
            switch (this.linkType) {
                case 'internal': resolved = this.internalUrl || ''; break
                case 'category': resolved = this.categoryUrl || ''; break
                case 'product': resolved = this.productUrl || ''; break
                case 'external': resolved = this.externalUrl; break
                case 'mailto': resolved = this.email ? `mailto:${this.email}` : ''; break
                case 'media': resolved = this.mediaUrl; break
                // Dynamic (template): the bare token — the backend fills the recipient's OWN order
                // per-send. Composer: the token names the chosen order by id; the backend mints the
                // signed URL from it at send (only if it's this member's). No order → empty.
                case 'order': resolved = this.isDynamicOrder ? '{{order.details_url}}' : (this.orderId ? `{{order.details_url:${this.orderId}}}` : ''); break
                case 'tracking': resolved = this.isDynamicOrder ? '{{order.tracking_url}}' : (this.orderId ? `{{order.tracking_url:${this.orderId}}}` : ''); break
            }
            this.lastEmitted = resolved
            this.$emit('input', resolved)
        }
    }
}
</script>

<style lang="sass" scoped>
.comm-link-picker
    display: flex
    flex-direction: column
    gap: 8px
    // The type switch can carry up to 7 options in a panel too narrow for one joined row.
    // Rather than let a joined el-radio-group wrap into broken half-buttons, render each
    // option as a discrete rounded pill with a small gap, so wrapping reads as intentional
    // while keeping the same active/inactive button language as the rest of the app.
    .comm-link-picker__types
        display: flex
        flex-wrap: wrap
        gap: 6px
        ::v-deep .el-radio-button
            margin: 0
            .el-radio-button__inner
                border: 1px solid #dcdfe6
                border-radius: 4px
                box-shadow: none
            &.is-active .el-radio-button__inner
                border-color: #409eff
    .comm-link-picker__body
        .flex-row
            align-items: center
    // Menu-name opt-group headers in the internal-page tree: muted, non-interactive labels
    // (the node is isDisabled), so the tree reads grouped by menu like the reference.
    ::v-deep label.optgroup
        color: $color-grey-text
        cursor: default
    .help
        display: block
        line-height: 1.4
        font-size: .75rem
        color: #909399
    .comm-link-picker__temp-hint
        display: flex
        align-items: center
        gap: 4px
        margin-top: 4px
        color: #e6a23c
        i
            font-size: .9em
</style>
